import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

export type MenuSection = 'header' | 'footer' | 'hamburger';
export type MenuItemKey =
  | 'alta-costura'
  | 'asesoria-imagen'
  | 'vestidos-novia'
  | 'tienda'
  | 'blog'
  | 'pret-a-porter'
  | 'contacto'
  | 'testimonios'
  | 'mi-reserva'
  | 'trajes-medida'
  | 'sobre-nosotros'
  | 'experiencia';

export interface MenuVisibilityState {
  header: Record<MenuItemKey, boolean>;
  footer: Record<MenuItemKey, boolean>;
  hamburger: Record<MenuItemKey, boolean>;
}

export interface MaintenanceConfig {
  enabled: boolean;
  message: string;
  eta: string;
  showAdminLink: boolean;
}

export interface StripeConfig {
  enabled: boolean;
  invoiceSurchargePercentage: number;
}

interface SiteSettingsContextType {
  visibility: MenuVisibilityState;
  maintenance: MaintenanceConfig;
  stripe: StripeConfig;
  loading: boolean;
  error: string | null;
  toggleVisibility: (section: MenuSection, key: MenuItemKey) => Promise<void>;
  setVisibilityAll: (section: MenuSection, visible: boolean) => Promise<void>;
  resetVisibility: () => Promise<void>;
  updateMaintenance: (config: MaintenanceConfig) => Promise<void>;
  updateStripeConfig: (config: StripeConfig) => Promise<void>;
  refresh: () => Promise<void>;
}

const VISIBILITY_ID = 'menu_visibility';
const MAINTENANCE_ID = 'maintenance_mode';
const STRIPE_ID = 'stripe_config';

const DEFAULT_VISIBLE_KEYS: MenuItemKey[] = [
  'alta-costura', 'asesoria-imagen', 'vestidos-novia', 'tienda', 'blog',
  'pret-a-porter', 'contacto', 'testimonios', 'mi-reserva', 'trajes-medida', 'sobre-nosotros', 'experiencia'
];

const DEFAULT_VISIBILITY: MenuVisibilityState = {
  header: Object.fromEntries(DEFAULT_VISIBLE_KEYS.map(k => [k, true])) as Record<MenuItemKey, boolean>,
  footer: Object.fromEntries(DEFAULT_VISIBLE_KEYS.map(k => [k, true])) as Record<MenuItemKey, boolean>,
  hamburger: Object.fromEntries(DEFAULT_VISIBLE_KEYS.map(k => [k, true])) as Record<MenuItemKey, boolean>,
};

const DEFAULT_MAINTENANCE: MaintenanceConfig = {
  enabled: false,
  message: 'Estamos realizando mejoras para ofrecerte una mejor experiencia.',
  eta: 'Volveremos pronto.',
  showAdminLink: true,
};

const DEFAULT_STRIPE: StripeConfig = {
  enabled: true,
  invoiceSurchargePercentage: 10,
};

const SiteSettingsContext = createContext<SiteSettingsContextType | undefined>(undefined);

export function SiteSettingsProvider({ children }: { children: React.ReactNode }) {
  const [visibility, setVisibility] = useState<MenuVisibilityState>(DEFAULT_VISIBILITY);
  const [maintenance, setMaintenance] = useState<MaintenanceConfig>(DEFAULT_MAINTENANCE);
  const [stripe, setStripe] = useState<StripeConfig>(DEFAULT_STRIPE);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSettings = useCallback(async () => {
    try {
      const { data, error: err } = await supabase
        .from('site_settings')
        .select('*')
        .in('id', [VISIBILITY_ID, MAINTENANCE_ID, STRIPE_ID]);

      if (err) throw err;

      const visRow = data?.find(r => r.id === VISIBILITY_ID);
      const maintRow = data?.find(r => r.id === MAINTENANCE_ID);
      const stripeRow = data?.find(r => r.id === STRIPE_ID);

      if (visRow?.value) {
        const v = visRow.value as Partial<MenuVisibilityState>;
        setVisibility({
          header: { ...DEFAULT_VISIBILITY.header, ...(v.header || {}) },
          footer: { ...DEFAULT_VISIBILITY.footer, ...(v.footer || {}) },
          hamburger: { ...DEFAULT_VISIBILITY.hamburger, ...(v.hamburger || {}) },
        });
      }

      if (maintRow?.value) {
        setMaintenance({ ...DEFAULT_MAINTENANCE, ...(maintRow.value as Partial<MaintenanceConfig>) });
      }

      if (stripeRow?.value) {
        setStripe({ ...DEFAULT_STRIPE, ...(stripeRow.value as Partial<StripeConfig>) });
      }
    } catch (err: any) {
      console.error('Error fetching site settings:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();

    // Subscribe to changes
    const channel = supabase
      .channel('public:site_settings')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'site_settings' }, () => {
        fetchSettings();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchSettings]);

  const toggleVisibility = async (section: MenuSection, key: MenuItemKey) => {
    const nextState = {
      ...visibility,
      [section]: { ...visibility[section], [key]: !visibility[section][key] }
    };
    setVisibility(nextState); // Optimistic update
    const { error: err } = await supabase.from('site_settings').upsert({ id: VISIBILITY_ID, value: nextState });
    if (err) setError(err.message);
  };

  const setVisibilityAll = async (section: MenuSection, visible: boolean) => {
    const nextState = {
      ...visibility,
      [section]: Object.fromEntries(DEFAULT_VISIBLE_KEYS.map(k => [k, visible])) as Record<MenuItemKey, boolean>
    };
    setVisibility(nextState);
    const { error: err } = await supabase.from('site_settings').upsert({ id: VISIBILITY_ID, value: nextState });
    if (err) setError(err.message);
  };

  const resetVisibility = async () => {
    setVisibility(DEFAULT_VISIBILITY);
    const { error: err } = await supabase.from('site_settings').upsert({ id: VISIBILITY_ID, value: DEFAULT_VISIBILITY });
    if (err) setError(err.message);
  };

  const updateMaintenance = async (config: MaintenanceConfig) => {
    setMaintenance(config);
    const { error: err } = await supabase.from('site_settings').upsert({ id: MAINTENANCE_ID, value: config });
    if (err) setError(err.message);
  };

  const updateStripeConfig = async (config: StripeConfig) => {
    setStripe(config);
    const { error: err } = await supabase.from('site_settings').upsert({ id: STRIPE_ID, value: config });
    if (err) setError(err.message);
  };

  return (
    <SiteSettingsContext.Provider value={{
      visibility, maintenance, stripe, loading, error,
      toggleVisibility, setVisibilityAll, resetVisibility, updateMaintenance, updateStripeConfig, refresh: fetchSettings
    }}>
      {children}
    </SiteSettingsContext.Provider>
  );
}

export function useSiteSettings() {
  const context = useContext(SiteSettingsContext);
  if (context === undefined) {
    throw new Error('useSiteSettings must be used within a SiteSettingsProvider');
  }
  return context;
}
